import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ClipPublic, Sport } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/shared/Button';
import { Input } from '../../components/shared/Input';
import { Icon } from '../../components/shared/Icons';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { apiErrorMessage } from '../../lib/api';
import { useSportsQuery } from '../../hooks/use-sports';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useAdminClipsQuery } from '../../hooks/use-clips';
import { useInfiniteScroll } from '../../hooks/use-infinite-scroll';
import { deriveRowState, statusLabel, STATUS_CHIP, type RowState } from './ClipsPage';

// Rows revealed per infinite-scroll step. The console fetches the whole sport-scoped library in
// one query, so this is pure client-side windowing over the filtered set.
const CLIPS_WINDOW = 20;

// super_admin's clips console differs from the federation_admin table: it spans every sport +
// federation, so the entry point is a sport picker (big cards). Pick a sport → the clip list is
// fetched server-scoped to it (?sport_id), then narrowed client-side by federation / status /
// search. Opening a clip goes to the read-only review page (video + discussion), not the tagging
// editor. The selected sport lives in the URL (?sport=<id>) so the view is linkable + survives a
// back-nav from the review page.
export function SuperAdminClipsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const sportId = params.get('sport') ?? undefined;

  const sportsQuery = useSportsQuery();
  const activeSport = sportsQuery.data?.find((s) => s.id === sportId);

  // An unknown / stale ?sport still shows the picker (activeSport is undefined) rather than an
  // empty list, so a deleted sport's link degrades gracefully.
  if (!sportId || !activeSport) {
    return (
      <>
        <PageHeader
          title={t('admin.clips.list.title')}
          sub={t('admin.clips.superAdminList.sub')}
        />
        <SportPicker
          sports={sportsQuery.data ?? []}
          isLoading={sportsQuery.isLoading}
          isError={sportsQuery.isError}
          error={sportsQuery.error}
          onPick={(id) => setParams({ sport: id })}
        />
      </>
    );
  }

  return <SportClips sport={activeSport} onBack={() => setParams({})} />;
}

// ─── Sport picker (the landing) ──────────────────────────────────────────────────

function SportPicker({
  sports,
  isLoading,
  isError,
  error,
  onPick,
}: {
  sports: Sport[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onPick: (sportId: string) => void;
}) {
  const { t } = useTranslation();
  if (isLoading) return <ListSkeleton rows={3} />;
  if (isError && sports.length === 0) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.clips.superAdminList.couldNotLoadSports'))}
      </div>
    );
  }
  if (sports.length === 0) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-16 text-center">
        <p className="text-sm text-muted">{t('admin.clips.superAdminList.noSportsYet')}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {sports.map((sport) => (
        <button
          key={sport.id}
          type="button"
          onClick={() => onPick(sport.id)}
          className="group flex flex-col gap-3 rounded-lg border border-line bg-surface p-5 text-left shadow-1 transition-colors hover:border-line-3 hover:bg-surface-2"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-11 w-11 place-items-center rounded-md bg-accent-tint text-accent">
              <Icon.whistle className="h-5 w-5" />
            </span>
            <Icon.chevronRight className="h-5 w-5 text-ink-3 transition-transform group-hover:translate-x-0.5" />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-ink">{sport.name}</h3>
            <p className="mt-0.5 line-clamp-1 text-[12.5px] text-muted">{sport.rulebook_name}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── Scoped clip list for the chosen sport ───────────────────────────────────────

interface ClipListFilters {
  federationId: string | 'all';
  status: RowState | 'all';
  // Managed-tag filter — derived from the loaded clips' embedded tags, so it spans every
  // federation's vocabulary for the chosen sport without a per-tenant /clip-tags fetch.
  tagId: string | 'all';
  search: string;
}

const INITIAL_LIST_FILTERS: ClipListFilters = { federationId: 'all', status: 'all', tagId: 'all', search: '' };

function SportClips({ sport, onBack }: { sport: Sport; onBack: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clipsQuery = useAdminClipsQuery({ sportId: sport.id });
  const federationsQuery = useFederationsQuery();

  // federation_id → name, so each clip row + the filter dropdown can show a readable tenant name
  // (the clip payload carries only the id). super_admin already has the federations list cached.
  const fedName = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of federationsQuery.data ?? []) map.set(f.id, f.name);
    return map;
  }, [federationsQuery.data]);

  const [filters, setFilters] = useState<ClipListFilters>(INITIAL_LIST_FILTERS);
  const allClips = clipsQuery.data ?? [];

  // Federation dropdown options = only the federations that actually have clips in this sport, so
  // the list doesn't offer tenants that would always return empty.
  const federationOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of allClips) {
      // Official clips (federation_id null) aren't a federation → they don't get a dropdown option.
      if (c.federation_id && !seen.has(c.federation_id)) {
        seen.set(c.federation_id, fedName.get(c.federation_id) ?? t('admin.clips.superAdminList.unknownFederationName'));
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allClips, fedName, t]);

  // Tag dropdown options = the unique managed tags across the loaded clips (one entry per id).
  const tagOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of allClips) {
      for (const t of c.tags ?? []) if (!seen.has(t.id)) seen.set(t.id, t.label);
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allClips]);

  const filtered = useMemo(() => applyListFilters(allClips, filters), [allClips, filters]);

  // Infinite-scroll window over the filtered set. Reset to the first page on any filter change so a
  // narrowed view doesn't inherit a window scrolled open under the previous filter.
  const [visibleCount, setVisibleCount] = useState(CLIPS_WINDOW);
  useEffect(() => {
    setVisibleCount(CLIPS_WINDOW);
  }, [filters]);
  const visibleClips = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = visibleCount < filtered.length;
  const sentinelRef = useInfiniteScroll(() => setVisibleCount((c) => c + CLIPS_WINDOW), hasMore);

  return (
    <>
      <PageHeader
        title={sport.name}
        sub={t('admin.clips.superAdminList.sportClipsSub')}
        actions={
          <Button variant="ghost" onClick={onBack}>
            <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('admin.clips.superAdminList.allSports')}
          </Button>
        }
      />

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded border border-line bg-surface p-2 shadow-1">
        <div className="relative min-w-[220px] flex-1">
          <Icon.search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            type="search"
            placeholder={t('admin.clips.list.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
            aria-label={t('admin.clips.list.searchAria')}
          />
        </div>
        <ListSelect
          label={t('admin.clips.superAdminList.federationLabel')}
          value={filters.federationId}
          onChange={(v) => setFilters({ ...filters, federationId: v })}
          options={[{ value: 'all', label: t('admin.clips.superAdminList.allFederations') }, ...federationOptions]}
        />
        <ListSelect
          label={t('admin.clips.list.filterStatus')}
          value={filters.status}
          onChange={(v) => setFilters({ ...filters, status: v as ClipListFilters['status'] })}
          options={[
            { value: 'all', label: t('admin.clips.list.filterAllStatuses') },
            { value: 'published', label: statusLabel(t, 'published') },
            { value: 'draft', label: statusLabel(t, 'draft') },
            { value: 'processing', label: statusLabel(t, 'processing') },
            { value: 'uploading', label: statusLabel(t, 'uploading') },
            { value: 'failed', label: statusLabel(t, 'failed') },
          ]}
        />
        {/* Tag filter only shows once some clip in this sport carries a tag. */}
        {tagOptions.length > 0 && (
          <ListSelect
            label={t('admin.clips.list.filterTag')}
            value={filters.tagId}
            onChange={(v) => setFilters({ ...filters, tagId: v })}
            options={[{ value: 'all', label: t('admin.clips.list.filterAllTags') }, ...tagOptions]}
          />
        )}
        {hasActiveListFilters(filters) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters(INITIAL_LIST_FILTERS)}
            aria-label={t('admin.clips.list.clearAria')}
          >
            <Icon.close className="h-3.5 w-3.5" /> {t('admin.clips.list.clearLabel')}
          </Button>
        )}
      </div>

      {clipsQuery.isLoading ? (
        <ListSkeleton rows={6} />
      ) : clipsQuery.isError && allClips.length === 0 ? (
        <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {apiErrorMessage(clipsQuery.error, t('admin.clips.list.couldNotLoad'))}
        </div>
      ) : allClips.length === 0 ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
          <p className="text-sm text-muted">{t('admin.clips.superAdminList.noClipsInSport', { sport: sport.name })}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
          <p className="text-sm text-muted">{t('admin.clips.list.emptyNoMatches')}</p>
          <Button variant="ghost" size="sm" className="mt-3" onClick={() => setFilters(INITIAL_LIST_FILTERS)}>
            {t('admin.clips.list.clearFilters')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* overflow-x-auto (not -hidden) so the row scrolls on a phone rather than crushing cells. */}
          <div className="overflow-x-auto rounded border border-line bg-surface">
            <table className="w-full min-w-[680px] text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
                  <th className="px-4 py-2.5 font-semibold">{t('admin.clips.superAdminList.colClip')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.clips.superAdminList.federationLabel')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colStatus')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.clips.list.colUploadedBy')}</th>
                  <th className="px-4 py-2.5" aria-label={t('admin.clips.superAdminList.colOpenAria')} />
                </tr>
              </thead>
              <tbody>
                {visibleClips.map((clip) => (
                  <ClipReviewRow
                    key={clip.id}
                    clip={clip}
                    federationName={
                      clip.federation_id
                        ? (fedName.get(clip.federation_id) ?? '—')
                        : t('admin.clips.superAdminList.officialFederationName')
                    }
                    onOpen={() => navigate(`/admin/clips/${clip.id}/review`)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Infinite-scroll sentinel — reveals the next window as it nears view. */}
          {hasMore && (
            <div ref={sentinelRef} className="grid place-items-center py-2 text-[11px] text-muted">
              <Spinner className="w-3.5 h-3.5" />
              <span className="mt-1">{t('admin.clips.list.loadingMore')}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// A row in the scoped list. Whole row navigates to the review page (super_admin's clip surface).
function ClipReviewRow({
  clip,
  federationName,
  onOpen,
}: {
  clip: ClipPublic;
  federationName: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const state = deriveRowState(clip);
  const inFlight = state === 'uploading' || state === 'processing';
  const uploader = clip.uploaded_by_user?.full_name ?? '—';

  return (
    <tr
      className="cursor-pointer border-b border-line transition-colors last:border-0 hover:bg-surface-2"
      onClick={onOpen}
    >
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="relative h-12 w-[80px] flex-none overflow-hidden rounded-sm border border-line-2 bg-bg-2">
            {clip.poster_url ? (
              <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center text-[9px] uppercase tracking-wider text-muted">
                {inFlight ? '…' : t('common.noPoster')}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-ink" title={clip.title}>{clip.title}</div>
            {clip.rule_article && (
              <div className="mt-0.5 line-clamp-1 text-[12px] text-muted">
                {t('common.ruleLabel', { number: clip.rule_article.article_number })} · {clip.rule_article.title}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5 text-ink-2">{federationName}</td>
      <td className="px-4 py-2.5">
        <span
          className={
            'inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium ' +
            STATUS_CHIP[state]
          }
        >
          {inFlight && <Spinner className="w-2.5 h-2.5" />}
          {statusLabel(t, state)}
        </span>
      </td>
      <td className="px-4 py-2.5 text-ink-2">{uploader}</td>
      <td className="px-3 py-2.5 text-right">
        <Icon.chevronRight className="ml-auto h-4 w-4 text-ink-3" />
      </td>
    </tr>
  );
}

// ─── Filter helpers ──────────────────────────────────────────────────────────────

function hasActiveListFilters(f: ClipListFilters): boolean {
  return f.federationId !== 'all' || f.status !== 'all' || f.tagId !== 'all' || f.search.trim() !== '';
}

function applyListFilters(clips: ClipPublic[], f: ClipListFilters): ClipPublic[] {
  const search = f.search.trim().toLowerCase();
  return clips.filter((c) => {
    if (f.federationId !== 'all' && c.federation_id !== f.federationId) return false;
    if (f.status !== 'all' && deriveRowState(c) !== f.status) return false;
    // Optional tag embed — coalesce so an untagged clip fails the filter instead of throwing.
    if (f.tagId !== 'all' && !(c.tags ?? []).some((t) => t.id === f.tagId)) return false;
    if (search && !c.title.toLowerCase().includes(search)) return false;
    return true;
  });
}

// Token-matched native select (mirrors the federation_admin filter bar's FilterSelect).
function ListSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={[
        'h-10 rounded-sm border border-line-2 bg-surface px-2.5 pr-7 text-sm text-ink',
        'outline-none transition-[border-color,box-shadow]',
        'focus:border-ink-3 focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--ink)_10%,transparent)]',
      ].join(' ')}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
